/**
 * 반복 출금 적요의 달 옮기기(#447) — 규칙 적요는 **기준 달(적요를 적은 때 아직 안 만든 첫 달)에 맞춰** 적고, 그 뒤 달은 지난 달 수만큼
 * 「N월」(앞에 「2026.」「26.」「2026년」 연도가 붙으면 연도까지)·「M/D」·한 달 안팎 점 날짜 기간을 민다. 「00월」·계약 기간(45일 넘는 점 날짜)은 그대로.
 * 🔴 서버 `functions/src/cashRuleParse.ts shiftRuleSummary` 와 같은 식 — 바꾸면 같이(`functions/scripts/b447-shift-test.mjs` 가 대조).
 */
export function shiftRuleSummary(text: string | null, baseMonth: string, month: string): string | null {
  if (!text) return text;
  const by = (Number(month.slice(0, 4)) - Number(baseMonth.slice(0, 4))) * 12
    + Number(month.slice(5, 7)) - Number(baseMonth.slice(5, 7));
  if (by <= 0) return text;
  // 「09월」처럼 0 을 붙여 쓴 자리만 0 을 붙인다(「10월」 → 「2월」).
  const pad = (n: number, like: string) => (like.length >= 2 && like[0] === '0' ? String(n).padStart(2, '0') : String(n));
  const lastDay = (m: number) => new Date(Date.UTC(Number(month.slice(0, 4)), m, 0)).getUTCDate();
  const step = (m: number) => { const k = m - 1 + by; return { carry: Math.floor(k / 12), m: (k % 12) + 1 }; };
  const yearOf = (ys: string, carry: number) => String((Number(ys) + carry) % (ys.length === 2 ? 100 : 10000)).padStart(ys.length, '0');
  // 점 날짜 기간 — 한 달 안팎(45일 이하)이면 요금 기간(「26.11.10~26.12.09」)이라 같이 민다. 더 길면 계약 기간이라 그대로.
  const dot = (ys: string, ms: string, ds: string) => {
    const y = Number(ys) + (ys.length === 2 ? 2000 : 0);
    const m = Number(ms);
    const d = Number(ds);
    return m >= 1 && m <= 12 && d >= 1 && d <= new Date(Date.UTC(y, m, 0)).getUTCDate() ? Date.UTC(y, m - 1, d) : null;
  };
  const shiftDot = (ys: string, ms: string, ds: string) => {
    const s = step(Number(ms));
    const y = Number(ys) + (ys.length === 2 ? 2000 : 0) + s.carry;
    const d = Math.min(Number(ds), new Date(Date.UTC(y, s.m, 0)).getUTCDate());
    return `${yearOf(ys, s.carry)}.${String(s.m).padStart(ms.length, '0')}.${String(d).padStart(ds.length, '0')}`;
  };
  return text
    .replace(/(?<![0-9.])([0-9]{4}|[0-9]{2})[.]([0-9]{1,2})[.]([0-9]{1,2})( ?[~-] ?)([0-9]{4}|[0-9]{2})[.]([0-9]{1,2})[.]([0-9]{1,2})(?![0-9.])/g,
      (all: string, y1: string, m1: string, d1: string, mid: string, y2: string, m2: string, d2: string) => {
        const a = dot(y1, m1, d1);
        const b = dot(y2, m2, d2);
        if (a === null || b === null || b < a || b - a > 45 * 86400000) return all;
        return `${shiftDot(y1, m1, d1)}${mid}${shiftDot(y2, m2, d2)}`;
      })
    // 연도는 「2026.」「26.」「2026-」「2026년 」 모양까지(#447 11-02 「2026년 11월」).
    .replace(/(?<![0-9])(?:([0-9]{4}|[0-9]{2})([./-]|년 ?))?([0-9]{1,2})( ?)(월|月)/g,
      (all: string, ys: string | undefined, sep: string | undefined, ms: string, sp: string, unit: string) => {
        const m = Number(ms);
        if (m < 1 || m > 12) return all;
        const s = step(m);
        const year = ys ? yearOf(ys, s.carry) + sep : '';
        return `${year}${ys ? String(s.m).padStart(2, '0') : pad(s.m, ms)}${sp}${unit}`; // 연도 붙은 달은 늘 두 자리(26.09월 · 2027년 01월)
      })
    .replace(/(?<![0-9./])([0-9]{1,2})[/]([0-9]{1,2})(?![0-9./])/g, (all: string, ms: string, ds: string) => {
      const m = Number(ms);
      const d = Number(ds);
      if (m < 1 || m > 12 || d < 1 || d > 31) return all;
      const s = step(m);
      return `${pad(s.m, ms)}/${pad(Math.min(d, lastDay(s.m)), ds)}`;
    });
}
